import type { ArticlePreviewModeState, PagePreviewModeState, PdfPreviewModeState, PopupPreviewModeState, PreviewModeState, SlidePreviewModeState } from '@autobid/ui/types/PreviewModeState'
import { useCheckUserPermission } from '@autobid/ui/composables/useCheckUserPermission'

export function useMyPreviewMode() {
  const APPAPI_KEY = useRuntimeConfig().APPAPI_KEY
  const route = useRoute()
  const { checkUserPermission } = useCheckUserPermission()
  const isAdmin = computed(() => checkUserPermission('edit_static'))

  const enabled = computed(() => {
    if (!(route.query.preview === 'true')) {
      return false
    }

    if (isAdmin.value) {
      return true
    }

    return Boolean(route.query.token && route.query.token === APPAPI_KEY)
  })

  const previewModeData = usePreviewMode({
    shouldEnable: () => {
      return enabled.value
    },
    getState: () => {
      const status = String(route.query.status ?? 'PUBLISHED') as PreviewModeState['status']
      const collection = String(route.query.collection ?? '') as PreviewModeState['collection']
      const path = String(route.query.path ?? '')

      switch (collection) {
        case 'articles':
          return {
            status,
            path,
            collection: collection as ArticlePreviewModeState['collection'],
            target: 'article',
          } satisfies ArticlePreviewModeState
        case 'pages':
        case 'alphabet-pages':
        case 'bmwgroup-pages':
        case 'bmwyuc-pages':
        case 'consulting-pages':
        case 'status-pages':
        case 'docs-pages':
        case 'mini-pages':
        case 'vwn-pages':
          return {
            status,
            path,
            collection: collection as PagePreviewModeState['collection'],
            target: 'page',
          } satisfies PagePreviewModeState
        case 'pdfs':
          return {
            status,
            path,
            collection: collection as PdfPreviewModeState['collection'],
            target: 'pdf',
          } satisfies PdfPreviewModeState
        case 'popups':
          return {
            status,
            path,
            collection: collection as PopupPreviewModeState['collection'],
            target: 'popup',
          } satisfies PopupPreviewModeState
        case 'slides':
          return {
            status,
            documentId: String(route.query.documentId ?? ''),
            collection: collection as SlidePreviewModeState['collection'],
            target: 'slide',
          } satisfies SlidePreviewModeState

        default:
          return null
      }
    },
  })

  watch(enabled, () => {
    previewModeData.enabled.value = enabled.value
  })

  return previewModeData
}
